import { Menu, Dropdown, Button, Modal, Input, Form } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { v4 } from "uuid";
import { Pages } from "../../../AppRouter";
import ApiService from "../../../core/service/api-service";
import { useTranslation } from "react-i18next";
import { DataGridProvider, useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import RowMenu from "../../../core/components/data-grid/RowMenu";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import ReportPreview from "../ReportPreview";
import ReportVersionsGrid from "../ReportVersionsGrid";
import { useModal } from "../../../core/context/ModalContext";
import ReportModal from "../../../pages/report/ReportModal";
import { ReportTemplateLanguages } from '../../hospital/hospital-data-grid/HospitalRowMenu';
import { useState } from "react";
import ReAssessmentRequestModal from "../../study/ReAssessmentRequestModal";
import TaskResolutionField, { hasOpenReAssessment, OpenTasksBox } from "../../study/TaskResolutionField";

const ButtonGroup = Button.Group;  // v5: antd/lib/button/button-group derin yolu yerine resmî API

const { TextArea } = Input;

export default function ReportRowMenu({ row }) {
    const { t } = useTranslation();
    const { refreshDataGrid } = useDataGrid();
    const { hasPermission } = useUser();
    const { openModal, closeModal } = useModal()
    const [reAssessmentModalVisible, setReAssessmentModalVisible] = useState(false);
    const [isRemoveConfirmation, setIsRemoveConfirmation] = useState(false);
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [confirmForm] = Form.useForm();

    const openReportPreviewModal = (studyId, requestId, reportId) => {
        openModal(t('Report Preview'), (
            <ReportPreview
                studyId={studyId}
                requestId={requestId}
                reportId={reportId}
                studyTasks={row.studyTasks}
                // Önizlemedeki "Kopyala & HBYS" raporun HBYS işaretini değiştiriyor; bu geçilmezse
                // grid haberdar olmuyor ve Hbys kutucuğu işaretsiz kalıyordu.
                onDataChanged={() => {
                    refreshDataGrid();
                }}
            />
        ))
    }

    const handleReAssessmentRequest = async () => {
        try {
            const values = await form.validateFields();
            const response = await ApiService.StudyTask.Create({
                studyId: row.studyId,
                requestId: row.requestId,
                taskType: 3, // REMOVE_REPORT_CONFIRMATION (yeniden degerlendirme kendi formunda)
                description: values.description
            });
            if (response.success) {
                setReAssessmentModalVisible(false);
                form.resetFields();
                refreshDataGrid();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    // Onay yalnız yeniden değerlendirmeyi kapatır (sonucu sorulur); eksik görüntü onayla kapanmaz, soru da sorulmaz.
    const handleConfirmClick = async () => {
        if (hasOpenReAssessment(row.studyTasks)) {
            setConfirmModalVisible(true);
        } else {
            await ApiService.Report.ConfirmReport(row.studyId, row.requestId, row.id);
            refreshDataGrid();
        }
    }

    const handleConfirmReport = async () => {
        try {
            const values = await confirmForm.validateFields();
            await ApiService.Report.ConfirmReport(row.studyId, row.requestId, row.id, { description: values.description });
            setConfirmModalVisible(false);
            confirmForm.resetFields();
            refreshDataGrid();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    }

    return (
        <>
            <RowMenu text={row.id}>
                <Menu>
                    {
                        hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (
                            <Menu.Item
                                key={'add-request-report'}
                                onClick={() => {
                                    openModal(t('Report'), (
                                        <ReportModal
                                            studyId={row.studyId}
                                            requestId={row.requestId}
                                            onRefreshGrid={refreshDataGrid}
                                        />
                                    ), null, window.innerWidth * 0.98, 20)
                                }}>
                                <div>
                                    {t('Request & Reports') + ' (R)'}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_LIST) && (
                            <Menu.Item
                                key={v4()}
                                onClick={() => { openReportPreviewModal(row.studyId, row.requestId, row.id) }}
                            >
                                {t('Report Preview')}
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_CONFIRM) && !row.isConfirm && (
                            <Menu.Item
                                key={v4()}>
                                <div onClick={handleConfirmClick}>
                                    {t('Confirm Report')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_REMOVE_CONFIRM) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}>
                                <div
                                    onClick={async () => {
                                        setIsRemoveConfirmation(true);
                                        setReAssessmentModalVisible(true);
                                    }}
                                >
                                    {t('Remove Confirmation')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}
                            >
                                <ButtonGroup>
                                    <Button
                                        size="small"
                                        style={{ padding: '0 8px', width: '180px' }}
                                        onClick={async (e) => {
                                            const response = await ApiService.ReportGenerator.GetReportPdf(row.studyId, row.requestId, row.id);
                                            if (response.success) {
                                                ApiService.downloadFile(response.data, response.fileName)
                                            }
                                        }}
                                    >
                                        {t('Download PDF')}
                                    </Button>
                                    {
                                        row?.reportOutputTemplates?.length > 1 && (
                                            <Dropdown
                                                overlay={
                                                    <Menu
                                                        onClick={async ({ key }) => {
                                                            const response = await ApiService.ReportGenerator.GetReportPdfWithLanguage(row.studyId, row.requestId, row.id, key);
                                                            if (response.success) {
                                                                ApiService.downloadFile(response.data, response.fileName)
                                                            }
                                                        }}
                                                    >
                                                        {
                                                            row?.reportOutputTemplates?.map(temp => (
                                                                <Menu.Item key={temp.language}>
                                                                    {t(`lang_${temp.language}`)}
                                                                </Menu.Item>
                                                            ))
                                                        }
                                                    </Menu>
                                                }
                                                trigger={['click']}
                                            >
                                                <Button
                                                    size="small"
                                                    style={{ padding: '0 8px' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DownOutlined />
                                                </Button>
                                            </Dropdown>
                                        )
                                    }
                                </ButtonGroup>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_DOWNLOAD_PDF) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}
                            >
                                <ButtonGroup>
                                    <Button
                                        size="small"
                                        style={{ padding: '0 8px', width: '180px' }}
                                        onClick={async (e) => {
                                            const response = await ApiService.ReportGenerator.GetReportWord(row.studyId, row.requestId, row.id)
                                            if (response.success) {
                                                ApiService.downloadFile(response.data, response.fileName)
                                            }
                                        }}
                                    >
                                        {t('Download Word')}
                                    </Button>
                                    {
                                        row?.reportOutputTemplates?.length > 1 && (
                                            <Dropdown
                                                overlay={
                                                    <Menu
                                                        onClick={async ({ key }) => {
                                                            const response = await ApiService.ReportGenerator.GetReportWordWithLanguage(row.studyId, row.requestId, row.id, key);
                                                            if (response.success) {
                                                                ApiService.downloadFile(response.data, response.fileName)
                                                            }
                                                        }}
                                                    >
                                                        {
                                                            row?.reportOutputTemplates?.map(temp => (
                                                                <Menu.Item key={temp.language}>
                                                                    {t(`lang_${temp.language}`)}
                                                                </Menu.Item>
                                                            ))
                                                        }
                                                    </Menu>
                                                }
                                                trigger={['click']}
                                            >
                                                <Button
                                                    size="small"
                                                    style={{ padding: '0 8px' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DownOutlined />
                                                </Button>
                                            </Dropdown>
                                        )
                                    }
                                </ButtonGroup>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.STUDY_REQUEST_DELETE) && !row.isConfirm && !row.isRequestDeleted && (
                            <Menu.Item
                                key={v4()}
                            >
                                <div
                                    onClick={async () => {
                                        const response = await ApiService.Request.DeleteRequest(row.studyId, row.requestId)
                                        if (response.success) {
                                            refreshDataGrid()
                                        }
                                    }}
                                >
                                    {t('Delete')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.STUDY_REQUEST_RESTORE_DELETED) && row.isRequestDeleted && (
                            <Menu.Item
                                key={v4()}
                            >
                                <div
                                    onClick={async () => {
                                        const response = await ApiService.Request.RestoreRequest(row.studyId, row.requestId)
                                        if (response.success) {
                                            refreshDataGrid()
                                        }
                                    }}
                                >
                                    {t('Restore')}
                                </div>
                            </Menu.Item>
                        )
                    }
                    {
                        hasPermission(PERMISSIONS.REPORT_VERSIONS) && (
                            <Menu.Item
                                key={'report-versions'}
                                // Versiyon yalnızca var olan rapor düzenlenince oluşur; hiç düzenlenmemiş
                                // raporda açılacak bir şey yok → gizlemek yerine pasif göster.
                                disabled={!row.reportVersionCount}
                                onClick={() => {
                                    openModal(t('Report Versions'), (
                                        <DataGridProvider>
                                            <ReportVersionsGrid
                                                studyId={row.studyId}
                                                reportId={row.id}
                                                requestId={row.requestId}
                                            />
                                        </DataGridProvider>
                                    ), null, window.innerWidth * 0.80, 20)
                                }}
                            >
                                {t('Report Versions')}
                            </Menu.Item>
                        )
                    }
                    {
                        (hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) || hasPermission(PERMISSIONS.STUDY_TASK_CREATE_RE_ASSESSMENT)) && row.isConfirm && (
                            <Menu.Item
                                key={v4()}
                            >
                                <div onClick={() => {
                                    setIsRemoveConfirmation(false);
                                    setReAssessmentModalVisible(true);
                                }}>
                                    {t('Re-Assessment Request')}
                                </div>
                            </Menu.Item>
                        )
                    }
                </Menu>
            </RowMenu>
            {/* Onay kaldirma (tip 3) serbest metinle devam eder; yeniden degerlendirme (tip 2)
                gerekce listesi + doktor secimi olan kendi formunu kullanir. */}
            <Modal
                title={t(isRemoveConfirmation ? 'Remove Confirmation' : 'Re-Assessment Request')}
                open={reAssessmentModalVisible}
                destroyOnClose={true}
                onOk={isRemoveConfirmation ? handleReAssessmentRequest : undefined}
                footer={isRemoveConfirmation ? undefined : null}
                onCancel={() => {
                    setReAssessmentModalVisible(false);
                    setIsRemoveConfirmation(false);
                    form.resetFields();
                }}
                okText={t('Submit')}
                cancelText={t('Cancel')}
            >
                {isRemoveConfirmation ? (
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="description"
                            label={t('Description')}
                            rules={[{ required: true, message: t('Please enter a description') }]}
                        >
                            <TextArea rows={4} placeholder={t('Enter reason for removing confirmation')} />
                        </Form.Item>
                    </Form>
                ) : (
                    <ReAssessmentRequestModal
                        studyId={row.studyId}
                        requestId={row.requestId}
                        onSaved={() => {
                            setReAssessmentModalVisible(false);
                            refreshDataGrid();
                        }}
                        onCancel={() => setReAssessmentModalVisible(false)}
                    />
                )}
            </Modal>
            <Modal
                title={t('Confirm Report - Open Task Exists')}
                open={confirmModalVisible}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    confirmForm.resetFields();
                }}
                onOk={handleConfirmReport}
                okText={t('Confirm')}
                cancelText={t('Cancel')}
            >
                <OpenTasksBox tasks={row.studyTasks} />
                <Form form={confirmForm} layout="vertical">
                    <TaskResolutionField tasks={row.studyTasks} />
                </Form>
            </Modal>
        </>
    )
}
